import React, {useEffect, useState} from "react";
import {Box, Button, Divider, Fade, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, ArrowDown01Icon, ArrowRight01Icon} from "@hugeicons/core-free-icons";
import {v4 as uuidv4} from "uuid";
import {Editor} from "@monaco-editor/react";
import {toast} from "react-toastify";
import {ParserModal} from "../../../../../components/modals/ParserModal";
import {useDebounce} from "../../../../../hooks/useDebounce";
import {UI, rgba} from "../../uiTokens";
import {fieldSx, iconBtnSx} from "../editMenuStyles";
import {SubSectionHeader} from "../components/EditMenuSection";

const DataType = {
    STRING: "string",
    OBJECT: "object",
};

export const StartNodeSettings = ({node, setNode, onUpdate, project}) => {
    const debounced = useDebounce(node, 500);

    useEffect(() => {
        if (debounced?.data?.global_params) {
            onUpdate(debounced);
        }
    }, [debounced?.data?.global_params]);

    const onUpdateGlobalParam = (param) => {
        setNode(prev => ({
            ...prev,
            data: {
                ...prev.data,
                global_params: prev?.data?.global_params?.map(i => (i?.id === param?.id ? {...param} : {...i})),
            },
        }));
    };

    const deleteParam = (uid) => {
        setNode(prev => ({
            ...prev,
            data: {
                ...prev.data,
                global_params: prev?.data?.global_params?.filter(i => i?.id !== uid),
            },
        }));
    };

    const checkGlobalParamName = (name) => {
        const filteredParams = node?.data?.global_params?.filter(i => i?.name === name);
        if (filteredParams?.length > 1) {
            toast.error("A global parameter with this name already exists. Please choose a different name to avoid overriding the existing parameter.");
        }
    };

    const addParam = () => {
        setNode(prev => {
            const state = {...prev};
            let global_params = [];
            if (Array.isArray(state?.data?.global_params) && state?.data?.global_params?.length > 0) {
                global_params = [...state.data.global_params];
            }
            global_params.push({id: uuidv4(), name: "", entity_type: null, data_type: DataType.STRING, value: ""});
            state.data.global_params = global_params;
            return state;
        });
    };

    return (
        <>
            <SubSectionHeader
                title="Global Params"
                action={
                    <Tooltip title="Add global param" disableInteractive>
                        <IconButton size="small" sx={iconBtnSx} onClick={addParam}>
                            <HugeiconsIcon icon={Add01Icon} size={16}/>
                        </IconButton>
                    </Tooltip>
                }
            />
            {node?.data?.global_params?.map((global_param, index) => (
                <GlobalParamItem
                    key={global_param?.id}
                    index={index}
                    global_param={global_param}
                    project={project}
                    onUpdateParam={onUpdateGlobalParam}
                    deleteParam={deleteParam}
                    checkGlobalParamName={checkGlobalParamName}
                />
            ))}
        </>
    );
};

const GlobalParamItem = ({index, global_param, project, checkGlobalParamName, onUpdateParam, deleteParam}) => {
    const [expandValue, setExpandValue] = useState(false);
    const [open, setOpen] = useState(false);

    const onUpdate = (key, value) => {
        onUpdateParam({...global_param, [key]: value});
    };

    const onChange = (e) => {
        const key = e?.target?.name;
        const value = e?.target?.value;
        onUpdate(key, value === "none" ? null : value);
    };

    const Row = ({label, children}) => (
        <Stack direction="row" gap={1} alignItems="center">
            <Typography sx={{flex: 1, fontSize: 12, color: UI.muted}}>{label}</Typography>
            <Box sx={{flex: 2}}>{children}</Box>
        </Stack>
    );

    return (
        <Box sx={{borderRadius: "10px", border: `1px solid ${rgba(UI.ink, 0.1)}`, backgroundColor: UI.surface, p: 1}}>
            <ParserModal
                handleClose={() => setExpandValue(false)}
                open={expandValue}
                onSuccess={(content) => onUpdate("value", content)}
                text={global_param?.value}
            />
            <Stack direction="row" gap={1} alignItems="center">
                <Typography noWrap sx={{flex: 1, fontWeight: 700, fontSize: 12.5, color: UI.ink}}>
                    {index + 1}. {global_param?.name || <em>unnamed</em>}
                </Typography>
                <IconButton size="small" sx={iconBtnSx} onClick={() => setOpen(!open)}>
                    {open ? <HugeiconsIcon icon={ArrowDown01Icon} size={16}/> : <HugeiconsIcon icon={ArrowRight01Icon} size={16}/>}
                </IconButton>
            </Stack>

            {open && (
                <Fade in={open}>
                    <Stack gap={1} sx={{mt: 1}}>
                        <Row label="Name">
                            <TextField
                                name="name" size="small" fullWidth sx={fieldSx}
                                value={global_param?.name}
                                onChange={onChange}
                                onBlur={() => checkGlobalParamName(global_param?.name)}
                            />
                        </Row>
                        <Row label="Entity Type">
                            <Select
                                name="entity_type" size="small" fullWidth sx={fieldSx}
                                value={global_param?.entity_type || "none"}
                                onChange={onChange}
                            >
                                <MenuItem value="none"><em>None</em></MenuItem>
                                {project?.entities?.map(entity => (
                                    <MenuItem key={entity?.id} value={entity?.name}>{entity?.name}</MenuItem>
                                ))}
                            </Select>
                        </Row>
                        <Row label="Data Type">
                            <Select
                                name="data_type" size="small" fullWidth sx={fieldSx}
                                value={global_param?.data_type || "none"}
                                onChange={onChange}
                            >
                                <MenuItem value={DataType.STRING}>String</MenuItem>
                                <MenuItem value={DataType.OBJECT}>Object</MenuItem>
                            </Select>
                        </Row>
                        <Stack gap={0.5}>
                            <Typography sx={{fontSize: 12, color: UI.muted}}>Value</Typography>
                            {global_param?.data_type === DataType.STRING ? (
                                <TextField
                                    name="value" size="small" fullWidth sx={fieldSx}
                                    value={global_param?.value}
                                    onChange={onChange}
                                    multiline rows={3}
                                />
                            ) : (
                                <Box
                                    onClick={() => setExpandValue(true)}
                                    sx={{
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        border: `1px solid ${rgba(UI.ink, 0.12)}`,
                                        cursor: "pointer",
                                        "&:hover": {borderColor: rgba(UI.accent, 0.4)},
                                    }}
                                >
                                    <Editor
                                        height="200px"
                                        defaultLanguage="javascript"
                                        value={global_param?.value || ""}
                                        options={{readOnly: true, minimap: {enabled: false}, fontSize: 12, scrollBeyondLastLine: false}}
                                    />
                                </Box>
                            )}
                        </Stack>
                        <Button
                            color="error"
                            size="small"
                            variant="outlined"
                            sx={{textTransform: "none", borderRadius: "8px"}}
                            onClick={() => deleteParam(global_param?.id)}
                        >
                            Delete Param
                        </Button>
                    </Stack>
                </Fade>
            )}
        </Box>
    );
};
